/**
 * Busca en roleDetalles (contexto: controller.roleDetalles = data.roles del login)
 * la fila cuyo módulo coincide con el texto enviado y devuelve si existe y sus valores.
 *
 *   const { encontrado, registro, valores } = getPermisosPorModulo(controller.roleDetalles, "Pagos");
 *   const same = usePermisosModulo("Pagos");
 */

import { useMaterialUIController } from "context";

/** Claves donde puede venir el nombre del módulo en cada fila (rolesusuarios). */
const CLAVES_MODULO = [
  "modulo",
  "Modulo",
  "nombre",
  "Nombre",
  "descripcion",
  "Descripcion",
  "clave",
  "Clave",
  "categoria",
  "Categoria",
];

function normalizar(txt) {
  if (txt === undefined || txt === null) return "";
  return String(txt).trim().toLowerCase();
}

/**
 * @param {Array<object>} roleDetalles
 * @param {string} modulo - nombre del módulo (ej. "Pagos")
 * @returns {{
 *   encontrado: boolean,
 *   registro: object | null,
 *   valores: Record<string, unknown>,
 * }}
 */
/** Normaliza quitando espacios/guiones para comparar alias (ej. "Registro Compras" ↔ registrocompras). */
function normalizarClave(txt) {
  return normalizar(txt).replace(/[\s_\-./]+/g, "");
}

function clavesFilaRol(row) {
  if (!row || typeof row !== "object") return [];
  const out = [row.programa];
  for (const clave of CLAVES_MODULO) {
    out.push(row[clave]);
  }
  return out.filter((k) => k !== undefined && k !== null && String(k).trim() !== "");
}

export function getPermisosPorModulo(roleDetalles, modulo) {
  const lista = Array.isArray(roleDetalles) ? roleDetalles : [];

  const pedido = normalizar(modulo);
  const pedidoCompacto = normalizarClave(modulo);
  if (!pedido) {
    return { encontrado: false, registro: null, valores: {} };
  }

  const registro =
    lista.find((row) => {
      for (const valor of clavesFilaRol(row)) {
        if (normalizar(valor) === pedido) return true;
        if (pedidoCompacto && normalizarClave(valor) === pedidoCompacto) return true;
      }
      return false;
    }) ?? null;

  if (!registro) {
    return { encontrado: false, registro: null, valores: {} };
  }

  const valores = {};
  return { encontrado: true, registro, valores };
}

/** Convierte el valor recibido del backend ('S'/'s'/1/true) a booleano. */
function esPermisoActivo(valor) {
  if (valor === true || valor === 1) return true;
  if (valor === null || valor === undefined) return false;
  const v = String(valor).trim().toLowerCase();
  return v === "s" || v === "si" || v === "1" || v === "true";
}

export function getPermisosPorModuloAccion(roleDetalles, modulo) {
  const lista = Array.isArray(roleDetalles) ? roleDetalles : [];

  const pedido = normalizar(modulo);

  if (!pedido) {
    return { encontrado: false, registro: null, valores: {} };
  }

  // Antes se devolvía solo la PRIMERA fila cuya categoria coincidiera: si esa
  // fila no traía el flag, el permiso se negaba aunque otra fila del mismo
  // módulo sí lo tuviera (dependía del orden del select del login). Ahora se
  // combinan todas las filas de la categoría: un flag está activo si CUALQUIER
  // fila lo tiene en 'S'. El backend sigue validando por programa.
  const filas = lista.filter((row) => normalizar(row.categoria) === pedido);

  if (!filas.length) {
    return { encontrado: false, registro: null, valores: {} };
  }

  const registro = { ...filas[0] };
  filas.slice(1).forEach((fila) => {
    ["modificar", "borrar", "versucursal", "insertar"].forEach((flag) => {
      if (esPermisoActivo(fila[flag])) registro[flag] = "S";
    });
  });

  const valores = {};
  return { encontrado: true, registro, valores };
}

/** Indica si existe una fila en roles para el módulo indicado (alias simple de getPermisosPorModulo). */
export function tienePermisosModulo(roleDetalles, modulo) {
  return getPermisosPorModulo(roleDetalles, modulo).encontrado;
}

/**
 * Marcador NEGATIVO "ocultarcostos": si el rol tiene una fila de ese programa
 * en rolesusuarios, NO debe ver costos de compra. Se eligió restar en vez de
 * un permiso positivo para que los roles existentes de todos los tenants
 * sigan viendo costos sin backfill; solo se agrega la fila a los restringidos.
 */
export function ocultaCostos(roleDetalles) {
  return tienePermisosModulo(roleDetalles, "ocultarcostos");
}

/**
 * Verifica si la fila del módulo tiene la columna `accion` activa.
 * Ej.: puedeAccion(controller.roleDetalles, "pagos", "borrar")
 */
export function puedeAccion(roleDetalles, modulo, accion) {
  const { registro } = getPermisosPorModuloAccion(roleDetalles, modulo);

  if (!registro) return false;
  return esPermisoActivo(registro[accion]);
}

/**
 * Verifica si la fila del programa (match por `programa`, ej. "ares4025")
 * tiene la columna `accion` activa. Es el equivalente de puedeAccion pero
 * buscando por programa en vez de por categoría, igual que valida el backend.
 * Ej.: puedeAccionPrograma(controller.roleDetalles, "ares4025", "borrar")
 */
export function puedeAccionPrograma(roleDetalles, programa, accion) {
  // Match ESTRICTO por columna `programa` (sin los alias por
  // modulo/descripcion/categoria de getPermisosPorModulo): los flags
  // gobiernan acciones destructivas y el backend valida por programa; un
  // alias por categoria leería el flag de otra fila del mismo grupo.
  const pedido = normalizar(programa);
  if (!pedido) return false;
  const lista = Array.isArray(roleDetalles) ? roleDetalles : [];
  const registro = lista.find((row) => normalizar(row?.programa) === pedido) ?? null;
  if (!registro) return false;
  return esPermisoActivo(registro[accion]);
}

/**
 * Claves con las que se valida una ruta del menú: `key`, `programa`, `aliases`
 * y las del padre (umbrella del collapse). Sin claves ⇒ ruta pública.
 */
export function tieneAccesoRuta(roleDetalles, route, parentKeys = []) {
  const propias = [
    route?.key,
    route?.programa,
    ...(Array.isArray(route?.keys) ? route.keys : []),
    ...(route?.aliases || []),
  ].filter((k) => k !== undefined && k !== null && String(k).trim() !== "");
  const heredadas = (Array.isArray(parentKeys) ? parentKeys : []).filter(
    (k) => k !== undefined && k !== null && String(k).trim() !== ""
  );
  const claves = [...propias, ...heredadas];
  if (claves.length === 0) return true;
  return claves.some((k) => tienePermisosModulo(roleDetalles, k));
}

/** Claves umbrella / alias del módulo Compras en roles. */
export const PROGRAMA_MANTENIMIENTO_COMPRAS = "mantenimientocompras";
export const ALIASES_COMPRAS = [
  "compras",
  "mantenimientocompras",
  "registrocompras",
  "recepcioncompras",
];

/** Lectura en pantallas de Compras: programa específico o cualquier alias del módulo. */
export function tienePermisosProgramaCompras(roleDetalles, programa) {
  if (!programa) return false;
  if (tienePermisosModulo(roleDetalles, programa)) return true;
  return ALIASES_COMPRAS.some((alias) => tienePermisosModulo(roleDetalles, alias));
}

/** Clave `programa` del collapse Productos / formulario principal (véase `routesFac`). */
export const PROGRAMA_GESTION_PRODUCTOS = "gestionproductos";

/**
 * Alta, edición o eliminar en una pantalla hija del módulo Productos: el rol incluye el `programa`
 * de esa pantalla o el programa de gestión principal del collapse.
 */
export function puedeEditarProgramaProductos(roleDetalles, programa) {
  if (!programa) return false;
  return (
    tienePermisosModulo(roleDetalles, programa) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_GESTION_PRODUCTOS)
  );
}

/** Clave `programa` del collapse Facturación / agrupador principal (véase `routesFac`). */
export const PROGRAMA_FACTURACION = "facturacion";

/**
 * Alta, edición o eliminar en una pantalla hija del módulo Facturación: el rol incluye
 * el `programa` específico de esa pantalla o el `programa` umbrella «facturacion».
 */
export function puedeEditarProgramaFacturacion(roleDetalles, programa) {
  if (!programa) return false;
  return (
    tienePermisosModulo(roleDetalles, programa) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_FACTURACION)
  );
}

/**
 * Flag de acción en una pantalla del módulo Facturación: la fila del
 * `programa` específico o la del umbrella «facturacion» tiene la columna
 * `accion` en 'S'. Contraparte con flags de puedeEditarProgramaFacturacion
 * (que solo mira presencia); el backend valida igual en RUTAS_PROTEGIDAS.
 */
export function puedeAccionFacturacion(roleDetalles, programa, accion) {
  if (!programa) return false;
  return (
    puedeAccionPrograma(roleDetalles, programa, accion) ||
    puedeAccionPrograma(roleDetalles, PROGRAMA_FACTURACION, accion)
  );
}

/** Igual que puedeAccionFacturacion pero con el umbrella «gestionproductos». */
export function puedeAccionProductos(roleDetalles, programa, accion) {
  if (!programa) return false;
  return (
    puedeAccionPrograma(roleDetalles, programa, accion) ||
    puedeAccionPrograma(roleDetalles, PROGRAMA_GESTION_PRODUCTOS, accion)
  );
}

/** Clave `programa` del collapse Configuración / agrupador principal. */
export const PROGRAMA_CONFIGURACION = "configuracion";

/**
 * Alta, edición o eliminar en una pantalla hija del módulo Configuración: el rol incluye
 * el `programa` específico de esa pantalla o el `programa` umbrella «configuracion».
 */
export function puedeEditarProgramaConfiguracion(roleDetalles, programa) {
  if (!programa) return false;
  return (
    tienePermisosModulo(roleDetalles, programa) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_CONFIGURACION)
  );
}

/** Igual que puedeAccionFacturacion pero con el umbrella «configuracion». */
export function puedeAccionConfiguracion(roleDetalles, programa, accion) {
  if (!programa) return false;
  return (
    puedeAccionPrograma(roleDetalles, programa, accion) ||
    puedeAccionPrograma(roleDetalles, PROGRAMA_CONFIGURACION, accion)
  );
}

/** Claves umbrella del módulo Contabilidad / Cuentas x Pagar / Bancos (véase `routescont.js`). */
export const PROGRAMA_CONTABILIDAD = 'contabilidad';
export const PROGRAMA_CUENTAS_X_PAGAR = 'cuentasxpagar';
export const PROGRAMA_BANCOS = 'bancos';

/**
 * Alta, edición o eliminar en una pantalla hija de Contabilidad / CxP / Bancos: el rol incluye
 * el `programa` específico o los umbrellas «contabilidad» / «cuentasxpagar» / «bancos».
 */
export function puedeEditarProgramaContabilidad(roleDetalles, programa) {
  if (!programa) return false;
  return (
    tienePermisosModulo(roleDetalles, programa) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_CONTABILIDAD) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_CUENTAS_X_PAGAR) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_BANCOS)
  );
}

/**
 * Sidenav y lectura de pantallas de Contabilidad: fila umbrella «contabilidad»
 * o AdmG / Administrador (el módulo se abre por licencia y esa fila puede
 * faltar, igual que en Nómina).
 */
export function puedeVerModuloContabilidad(roleDetalles, roleWeb) {
  return (
    esRolAdministrador(roleWeb) || tienePermisosModulo(roleDetalles, PROGRAMA_CONTABILIDAD)
  );
}

/** Clave umbrella del módulo Nómina (véase `routesnom.js`). */
export const PROGRAMA_NOMINA = 'nomina';
export const PROGRAMA_CONFIGURACION_NOMINA = 'configuracionnomina';
export const PROGRAMA_CATALOGOS_NOMINA = 'catalogosnomina';
export const PROGRAMA_DEPARTAMENTO_NOMINA = 'departamentonomina';
export const PROGRAMA_SECTOR_NOMINA = 'sectornomina';
export const PROGRAMA_REGISTRO_EMPLEADOS = 'registroempleados';
export const PROGRAMA_NOVEDADES_NOMINA = 'novedadesnomina';
export const PROGRAMA_PRESTAMOS_NOMINA = 'prestamosnomina';
export const PROGRAMA_INICIO_NOMINA = 'inicionomina';
/** Acciones → Iniciar nómina (fps8000a). Distinto de la pantalla Inicio. */
export const PROGRAMA_INICIAR_NOMINA = 'iniciarnomina';
/** Acciones → Definición de períodos (fps1070). */
export const PROGRAMA_DEFINICION_NOMINA = 'definicionnomina';
/** Acciones → Aplicar descuentos (fps8010). */
export const PROGRAMA_APLICAR_DESCUENTOS = 'aplicardescuentos';
/** Reportes → Listado de ingresos extras (fps8040). */
export const PROGRAMA_LISTADO_INGRESOS = 'listadoingresos';
/** Reportes → Listado de otros descuentos (fps8050). */
export const PROGRAMA_LISTADO_OTROS_DESCUENTOS = 'listadootrosdescuentos';
/** Acciones / Configuración → Excluir empleados (fps9010). */
export const PROGRAMA_EXCLUIR_EMPLEADOS = 'excluirempleados';
/** Acciones / Configuración → Cerrar nómina (fps8030). */
export const PROGRAMA_CERRAR_NOMINA = 'cerrarnomina';

/** AdmG / Administrador: ven el módulo nuevo aunque aún no esté sembrado en rolesusuarios. */
export function esRolAdministrador(roleWeb) {
  return ["AdmG", "Administrador"].includes(String(roleWeb || "").trim());
}

/**
 * El sidenav y las pantallas de Nómina se muestran si el rol tiene la fila
 * umbrella «nomina» o si es administrador (el módulo es nuevo y esa fila
 * todavía no existe en la mayoría de tenants).
 */
export function puedeVerModuloNomina(roleDetalles, roleWeb) {
  return (
    esRolAdministrador(roleWeb) || tienePermisosModulo(roleDetalles, PROGRAMA_NOMINA)
  );
}

/**
 * Alta, edición o eliminar en una pantalla hija de Nómina: el rol incluye
 * el `programa` específico, el umbrella «nomina» o es administrador.
 */
export function puedeEditarProgramaNomina(roleDetalles, programa, roleWeb) {
  if (esRolAdministrador(roleWeb)) return true;
  if (!programa) return false;
  return (
    tienePermisosModulo(roleDetalles, programa) ||
    tienePermisosModulo(roleDetalles, PROGRAMA_NOMINA)
  );
}

/**
 * Pantallas de Nómina con permiso propio: solo la fila de ese programa,
 * igual que Facturas Emitidas (`tienePermisosModulo`). No hereda «nomina»
 * ni el tipo AdmG/Administrador.
 */
export function puedeVerProgramaNominaPropio(roleDetalles, programa) {
  return Boolean(programa) && tienePermisosModulo(roleDetalles, programa);
}

function tieneFilaProgramaExacta(roleDetalles, programa) {
  const lista = Array.isArray(roleDetalles) ? roleDetalles : [];
  const pedido = normalizarClave(programa);
  if (!pedido) return false;
  return lista.some((row) => {
    const valor = normalizarClave(row?.programa ?? row?.Programa ?? row?.PROGRAMA);
    return valor === pedido;
  });
}

/**
 * Acciones → Iniciar nómina: solo la fila `iniciarnomina` en rolesusuarios.
 * No usa `inicionomina` (esa es la pantalla Inicio del menú), ni «nomina»,
 * ni AdmG/Administrador.
 */
export function puedeUsarIniciarNomina(roleDetalles) {
  return tieneFilaProgramaExacta(roleDetalles, PROGRAMA_INICIAR_NOMINA);
}

/**
 * Acciones → Definición de nómina: solo la fila `definicionnomina`.
 * No hereda «nomina», «nominainicial» ni AdmG/Administrador.
 */
export function puedeUsarDefinicionNomina(roleDetalles) {
  return tieneFilaProgramaExacta(roleDetalles, PROGRAMA_DEFINICION_NOMINA);
}

/**
 * Acciones → Aplicar descuentos: solo la fila `aplicardescuentos`.
 */
export function puedeUsarAplicarDescuentos(roleDetalles) {
  return tieneFilaProgramaExacta(roleDetalles, PROGRAMA_APLICAR_DESCUENTOS);
}

/**
 * Reportes → Listado de ingresos: solo la fila `listadoingresos`.
 */
export function puedeUsarListadoIngresos(roleDetalles) {
  return tieneFilaProgramaExacta(roleDetalles, PROGRAMA_LISTADO_INGRESOS);
}

/**
 * Reportes → Listado de otros descuentos: solo la fila `listadootrosdescuentos`.
 */
export function puedeUsarListadoOtrosDescuentos(roleDetalles) {
  return tieneFilaProgramaExacta(roleDetalles, PROGRAMA_LISTADO_OTROS_DESCUENTOS);
}

/**
 * Acciones → Excluir empleados: solo la fila `excluirempleados`.
 */
export function puedeUsarExcluirEmpleados(roleDetalles) {
  return tieneFilaProgramaExacta(roleDetalles, PROGRAMA_EXCLUIR_EMPLEADOS);
}

/**
 * Acciones → Cerrar nómina: solo la fila `cerrarnomina`.
 * No hereda «nomina», «iniciarnomina» ni AdmG/Administrador.
 */
export function puedeUsarCerrarNomina(roleDetalles) {
  return tieneFilaProgramaExacta(roleDetalles, PROGRAMA_CERRAR_NOMINA);
}

/**
 * Indica si un permiso concreto está activo (acepta S/s/1/true/"si").
 *
 * @param {unknown} valor
 */
export function usePermisosModulo(modulo) {
  const [controller] = useMaterialUIController();
  return getPermisosPorModulo(controller.roleDetalles, modulo);
}
